import { useEffect, useRef, useState, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Sparkles } from "@react-three/drei";
import * as THREE from "three";

const vertexShader = `
  uniform float uTime;
  uniform vec3 uMouse;
  uniform float uSize;
  uniform float uScale;
  varying vec3 vColor;
  varying float vForce;

  void main() {
    vColor = color;
    vec3 pos = position;

    pos.x += sin(uTime * 1.5 + position.y * 3.0) * 0.008;
    pos.y += cos(uTime * 1.2 + position.x * 3.0) * 0.008;

    vec3 diff = pos - uMouse;
    float dist = length(diff);
    float force = smoothstep(1.15, 0.0, dist);
    vForce = force;
    pos += normalize(diff + vec3(0.0001)) * force * 0.6;
    pos.z += force * 0.35;

    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = uSize * (uScale / -mv.z);
  }
`;

const fragmentShader = `
  varying vec3 vColor;
  varying float vForce;

  void main() {
    float d = length(gl_PointCoord - 0.5);
    if (d > 0.5) discard;
    float alpha = smoothstep(0.5, 0.08, d);
    vec3 col = mix(vColor, vec3(1.0, 0.72, 0.0), vForce * 0.7);
    gl_FragColor = vec4(col, alpha * (0.9 + vForce * 0.1));
  }
`;

function useLogoGeometry() {
  const [geometry, setGeometry] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.src = "/fenrion-logo.png";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, img.width, img.height).data;

      const positions = [];
      const colors = [];
      const step = Math.max(2, Math.floor(img.width / 240));
      const scale = 4.6 / img.width;

      for (let y = 0; y < img.height; y += step) {
        for (let x = 0; x < img.width; x += step) {
          const i = (y * img.width + x) * 4;
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const brightness = (r + g + b) / 3;
          if (brightness > 55) {
            const relief = (brightness / 255) * 0.55;
            positions.push(
              (x - img.width / 2) * scale,
              -(y - img.height / 2) * scale,
              relief + (Math.random() - 0.5) * 0.22
            );
            colors.push(r / 255, g / 255, b / 255);
          }
        }
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
      geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
      setGeometry(geo);
    };
  }, []);

  return geometry;
}

function WolfLogo() {
  const geometry = useLogoGeometry();
  const group = useRef();
  const mouse = useRef({ x: 0, y: 0 });
  const worldMouse = useRef(new THREE.Vector3(999, 999, 0));

  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
          uTime: { value: 0 },
          uMouse: { value: new THREE.Vector3(999, 999, 0) },
          uSize: { value: 0.05 },
          uScale: { value: 400 },
        },
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        vertexColors: true,
      }),
    []
  );

  useEffect(() => {
    const fn = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener("mousemove", fn, { passive: true });
    return () => window.removeEventListener("mousemove", fn);
  }, []);

  useEffect(
    () => () => {
      geometry?.dispose();
      material.dispose();
    },
    [geometry, material]
  );

  useFrame((state, delta) => {
    if (!group.current) return;
    const t = state.clock.elapsedTime;

    const targetRotY = Math.sin(t * 0.35) * 0.55 + mouse.current.x * 0.45;
    const targetRotX = mouse.current.y * -0.22 + Math.sin(t * 0.5) * 0.05;
    group.current.rotation.y = THREE.MathUtils.damp(group.current.rotation.y, targetRotY, 2.2, delta);
    group.current.rotation.x = THREE.MathUtils.damp(group.current.rotation.x, targetRotX, 2.2, delta);
    const vw = state.viewport.width;
    const targetX = vw > 9 ? 2.4 : vw > 7 ? 1.7 : 0;
    const targetScale = vw > 9 ? 1.18 : vw > 7 ? 0.92 : 0.62;
    group.current.position.x = THREE.MathUtils.damp(group.current.position.x, targetX, 2.5, delta);
    group.current.scale.setScalar(
      THREE.MathUtils.damp(group.current.scale.x, targetScale, 2.5, delta)
    );

    const v = worldMouse.current.set(mouse.current.x, mouse.current.y, 0.5).unproject(state.camera);
    const dir = v.sub(state.camera.position).normalize();
    const distance = -state.camera.position.z / dir.z;
    const world = state.camera.position.clone().add(dir.multiplyScalar(distance));
    group.current.worldToLocal(world);
    material.uniforms.uMouse.value.lerp(world, 1 - Math.exp(-8 * delta));

    material.uniforms.uTime.value = t;
    material.uniforms.uSize.value = 0.05 + Math.sin(t * 1.4) * 0.006;
    material.uniforms.uScale.value = (state.size.height / 2) * state.viewport.dpr;
  });

  if (!geometry) return null;

  return (
    <group ref={group}>
      <Float speed={1.4} rotationIntensity={0.12} floatIntensity={0.7}>
        <points geometry={geometry} material={material} />
      </Float>
    </group>
  );
}

export default function HeroCanvas() {
  return (
    <div className="absolute inset-0 pointer-events-none opacity-45 md:opacity-100" aria-hidden="true" data-testid="hero-canvas">
      <Canvas
        dpr={[1, 2.5]}
        camera={{ position: [0, 0, 6.5], fov: 42 }}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <WolfLogo />
        <Sparkles count={130} scale={11} size={2} speed={0.35} color="#FFB800" opacity={0.5} />
        <Sparkles count={50} scale={9} size={3.2} speed={0.2} color="#FF5A00" opacity={0.35} />
      </Canvas>
    </div>
  );
}
